<template>
  <section class="mt-1 mr-1">
    <section class="collection-header is-flex is-justify-content-space-between is-align-items-center px-3">
      <span class="has-text-weight-semibold">
        {{ collection.name }}
      </span>
      <button :disabled="isSearchMode"
              type="button"
              class="button collection-toggle-btn"
              @click="$emit('toggle')">
        <span v-if="collection.partsLoading || isExpandingAll || isSearching"
              class="icon is-small">
          <i class="fas fa-spinner fa-spin" />
        </span>
        <span v-else
              class="icon is-small">
          <i :class="expanded ? 'fas fa-minus' : 'fas fa-plus'" />
        </span>
      </button>
    </section>

    <section v-if="expanded">
      <h4 v-if="!!collection.description"
          class="m-2">
        {{ collection.description }}
      </h4>

      <section class="is-flex is-justify-content-flex-end my-4">
        <button v-if="canAddAllToCart"
                :disabled="isSearchMode"
                class="button mr-1"
                @click="$emit('addAllToCart', collection.parts)">
          {{ $t('addAllPartsCart') }}
        </button>
        <button :disabled="isSearchMode"
                class="button"
                @click="$emit('exportParts', collection.id)">
          {{ $t('exportPartsXlsx') }}
        </button>
      </section>

      <div v-if="collection.partsLoading || isExpandingAll || isSearching"
           class="p-3">
        {{ $t('loading') }}
      </div>

      <div v-else
           ref="partsWrapper"
           class="parts-list-wrapper drag-scroll pb-viewport-sm"
           :style="partsWrapperStyle"
           @mousedown="onMouseDown"
           @mousemove="onMouseMove"
           @mouseup="onMouseUp"
           @mouseleave="onMouseUp"
           @scroll="onPartsScroll">
        <div :style="{ minWidth: `${totalRowWidth}px` }">
          <div class="parts-header-row">
            <div v-if="!hideThumbnail"
                 class="part-cell"
                 style="width: 48px" />
            <div class="part-cell"
                 style="width: 56px" />
            <div v-for="column in partColumns"
                 class="part-cell"
                 :key="column"
                 :style="getColumnStyle(column)">
              <div v-if="pricingColumns.has(column)"
                   class="price-header">
                <label v-if="getShowPrice(column)">
                  {{ getColumnTr(column) }}
                </label>
                <span v-if="!isPriceLocked()"
                      class="icon is-small has-text-link eye-icon"
                      @click="handleClickHidePrice(column)">
                  <i :class="getShowPrice(column) ? 'fa fa-eye-slash' : 'fa fa-eye'" />
                </span>
              </div>
              <template v-else>
                <span>{{ getColumnTr(column) }}</span>
              </template>
            </div>
          </div>

          <template v-if="isMobileView">
            <div v-if="!collection.parts.length"
                 class="no-results-row">
              {{ $t('noResults') }}
            </div>

            <div v-for="part in collection.parts"
                 :key="part.id"
                 class="part-row"
                 :class="{ 'part-row--alt': isAltRow(part.id) }">
              <div v-if="!hideThumbnail"
                   class="part-cell thumbnail-cell"
                   style="width: 48px">
                <div v-if="part.thumbnailUrl"
                     class="is-flex is-align-items-center is-justify-content-center">
                  <figure class="image is-32x32 is-flex is-align-items-center">
                    <img :src="part.thumbnailUrl">
                  </figure>
                </div>
                <figure v-else
                        class="image is-32x32 part-thumbnail" />
              </div>

              <div class="part-cell"
                   style="width: 56px">
                <a v-if="part.isOrderable"
                   data-unit="part-collection-add-to-cart-button"
                   @click="$emit('addToCart', part)">
                  <span class="icon base" style="max-width: 3rem;">
                    <span class="icon is-medium">
                      <i class="fas fa-cart-plus fa-lg" />
                    </span>
                  </span>
                </a>
              </div>

              <div v-for="column in partColumns"
                   class="part-cell"
                   :key="`${part.id}-${column}`"
                   :style="getColumnStyle(column)">
                <template v-if="isPartLinkColumn(column)">
                  <span v-if="isWidget"
                        :class="{ 'has-text-link': hasInfoLandingPage, 'pointer-on-hover': hasInfoLandingPage }"
                        @click="openWidgetPartInfo(part)">
                    {{ part[column] }}
                  </span>
                  <router-link  v-else
                                :to="getPartHref(part)">
                    {{ part[column] }}
                  </router-link>
                </template>

                <template v-else-if="column === 'quantity'">
                  <span>{{ part.quantity }} {{ part.uom }}</span>
                </template>

                <template v-else-if="column.startsWith('tag:')">
                  <span>{{ getTagValues(part, column) }}</span>
                </template>

                <template v-else-if="pricingColumns.has(column)">
                  <label v-if="!getShowPrice(column)" />
                  <label v-else-if="isZero(part, column)">{{ $t('noPrice') }}</label>
                  <label v-else>{{ part[column] }}</label>
                </template>

                <template v-else>
                  <span>{{ part[column] }}</span>
                </template>
              </div>
            </div>
          </template>

          <MeasuredVirtualList v-else
                               :total-height="totalPartsHeight"
                               :offset-top="partsOffsetTop">
            <div v-if="!collection.parts.length"
                 class="no-results-row">
              {{ $t('noResults') }}
            </div>

            <div v-for="part in renderedParts"
                 :key="part.id"
                 :ref="el => setPartElement(part.id, el as Element | null)"
                 class="part-row"
                 :class="{ 'part-row--alt': isAltRow(part.id) }">
              <div v-if="!hideThumbnail"
                   class="part-cell thumbnail-cell"
                   style="width: 48px">
                <part-collection-thumbnail :is-widget="isWidget"
                                           :thumbnail-url="part.thumbnailUrl"
                                           @preview="emit('previewThumbnail', part)" />
              </div>

              <div class="part-cell"
                   style="width: 56px">
                <a v-if="part.isOrderable"
                   data-unit="part-collection-add-to-cart-button"
                   @click="$emit('addToCart', part)">
                  <span class="icon base" style="max-width: 3rem;">
                    <span class="icon is-medium">
                      <i class="fas fa-cart-plus fa-lg" />
                    </span>
                  </span>
                </a>
              </div>

              <div v-for="column in partColumns"
                   class="part-cell"
                   :key="`${part.id}-${column}`"
                   :style="getColumnStyle(column)">
                <template v-if="isPartLinkColumn(column)">
                  <span v-if="isWidget"
                        :class="{ 'has-text-link': hasInfoLandingPage, 'pointer-on-hover': hasInfoLandingPage }"
                        @click="openWidgetPartInfo(part)">
                    {{ part[column] }}
                  </span>
                  <router-link  v-else
                                :to="getPartHref(part)">
                    {{ part[column] }}
                  </router-link>
                </template>

                <template v-else-if="column === 'quantity'">
                  <span>{{ part.quantity }} {{ part.uom }}</span>
                </template>

                <template v-else-if="column.startsWith('tag:')">
                  <span>{{ getTagValues(part, column) }}</span>
                </template>

                <template v-else-if="pricingColumns.has(column)">
                  <label v-if="!getShowPrice(column)" />
                  <label v-else-if="isZero(part, column)">{{ $t('noPrice') }}</label>
                  <label v-else>{{ part[column] }}</label>
                </template>

                <template v-else>
                  <span>{{ part[column] }}</span>
                </template>
              </div>
            </div>
          </MeasuredVirtualList>
        </div>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useStore } from 'vuex'
import MeasuredVirtualList from '@/components/common/MeasuredVirtualList.vue'
import PartCollectionThumbnail from './PartCollectionThumbnail.vue'
import { useMeasuredVirtualList } from '@/composables/useMeasuredVirtualList'
import { useInfoLandingPage } from '@/components/widgets/composables/useInfoLandingPage'
import type {
  DisplayCollection,
  PartRow
} from '@/interfaces/library/partCollection'
import { getColumnTr } from '@/helpers/part'

// --------------------------------------------------
// Props / emits / composables
// --------------------------------------------------

const props = withDefaults(defineProps<{
  canAddAllToCart: boolean
  collection: DisplayCollection
  columnWidths: Record<string, number>
  contentWidth: number
  expanded: boolean
  hasHidePrice: boolean
  hideThumbnail?: boolean
  isExpandingAll: boolean
  isSearching: boolean
  isSearchMode: boolean
  isWidget?: boolean
  partColumns: string[]
}>(), {
  hideThumbnail: false,
  isWidget: false
})

const emit = defineEmits<{
  (e: 'toggle'): void,
  (e: 'addToCart', part: PartRow): void,
  (e: 'addAllToCart', parts: PartRow[]): void,
  (e: 'exportParts', collectionId: number): void,
  (e: 'toggleShowRetailPrice'): void,
  (e: 'toggleShowWholesalePrice'): void,
  (e: 'toggleShowDiscountPrice'): void,
  (e: 'previewThumbnail', part: PartRow): void
}>()

const store = useStore()
const isWidget = computed(() => store.state.auth.isWidget)
const { openPartInfoPage: openWidgetPartInfo, hasInfoLandingPage } = useInfoLandingPage()

// --------------------------------------------------
// Constants
// --------------------------------------------------

const thumbnailColumnWidth = 48
const cartColumnWidth = 56
const defaultColumnWidth = 120
const estimatedPartRowHeight = 40
const partsListMaxHeight = 500
const virtualListOverscanPx = 400
const mobileBreakpointPx = 768

const pricingColumns = new Set([
  'retailPrice',
  'wholesalePrice',
  'discountPrice'
])

// --------------------------------------------------
// Responsive mode
// --------------------------------------------------

const isMobileView = ref(false)

function syncIsMobileView() {
  isMobileView.value = window.matchMedia(`(max-width: ${mobileBreakpointPx}px)`).matches
}

onMounted(() => {
  syncIsMobileView()
  window.addEventListener('resize', syncIsMobileView)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncIsMobileView)
})

// --------------------------------------------------
// Layout
// --------------------------------------------------

const partsWrapperStyle = computed(() => {
  if (isMobileView.value) return undefined

  return {
    maxHeight: `${partsListMaxHeight}px`
  }
})

const totalRowWidth = computed(() => {
  const thumbnailWidth = props.hideThumbnail ? 0 : thumbnailColumnWidth
  const columnsWidth = props.partColumns.reduce(
    (sum, column) => {
      if (pricingColumns.has(column) && !getShowPrice(column)) return sum + 32
      return sum + (props.columnWidths[column] ?? defaultColumnWidth)
    },
    0
  )

  return thumbnailWidth + cartColumnWidth + columnsWidth
})

// --------------------------------------------------
// Parts virtualization
// --------------------------------------------------

const partsWrapper = ref<HTMLElement | null>(null)

const {
  totalHeight: totalPartsHeight,
  offsetTop: partsOffsetTop,
  renderedItems: renderedParts,
  syncViewportHeight: syncPartsViewport,
  onScroll: onPartsScroll,
  setItemElement: setPartElement,
} = useMeasuredVirtualList<PartRow>({
  items: computed(() => props.collection.parts),
  scrollRef: partsWrapper,
  getItemId: part => part.id,
  estimatedItemHeight: () => estimatedPartRowHeight,
  overscanPx: virtualListOverscanPx,
})

watch(partsWrapper, el => {
  if (el) nextTick(() => syncPartsViewport())
})

watch(isMobileView, () => {
  if (!isMobileView.value) nextTick(() => syncPartsViewport())
})

// --------------------------------------------------
// Row display
// --------------------------------------------------

const partIndexMap = computed(() => {
  const map = new Map<number, number>()
  props.collection.parts.forEach((part, index) => map.set(part.id, index))
  return map
})

function isAltRow(partId: number): boolean {
  return ((partIndexMap.value.get(partId) ?? 0) % 2) !== 0
}

// --------------------------------------------------
// Drag scroll
// --------------------------------------------------

let isDragging = false
let startX = 0
let scrollLeft = 0

function onMouseDown(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement
  const clickedElement = event.target as HTMLElement

  // Don't enable drag if clicking on text elements (spans/labels) or links
  if (clickedElement.tagName === 'SPAN' ||
      clickedElement.tagName === 'LABEL' ||
      clickedElement.tagName === 'A' ||
      clickedElement.tagName === 'BUTTON' ||
      clickedElement.closest('a, button')) {
    return // Allow text selection and link interaction
  }

  isDragging = true
  startX = event.pageX - target.offsetLeft
  scrollLeft = target.scrollLeft

  target.style.cursor = 'grabbing'
  target.style.userSelect = 'none'
}

function onMouseMove(event: MouseEvent) {
  if (!isDragging) return

  const target = event.currentTarget as HTMLElement
  const x = event.pageX - target.offsetLeft
  const walk = x - startX

  target.scrollLeft = scrollLeft - walk
}

function onMouseUp(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement

  isDragging = false
  target.style.cursor = 'grab'
  target.style.userSelect = ''
}

// --------------------------------------------------
// Pricing
// --------------------------------------------------

const hasHidePriceDisabled = computed(() => store.getters['user/hasHidePriceDisabled'])

function isPriceLocked(): boolean {
  return !props.hasHidePrice || hasHidePriceDisabled.value
}

function getColumnStyle(column: string): object {
  if (pricingColumns.has(column) && !getShowPrice(column)) {
    return { width: '32px', minWidth: '32px', maxWidth: '32px', flexGrow: '0' }
  }
  return props.columnWidths[column] != null ? { width: `${props.columnWidths[column]}px` } : {}
}

function getShowPrice(column: string) {
  switch (column) {
    case 'retailPrice':
      return props.collection.showRetail
    case 'wholesalePrice':
      return props.collection.showWholesale
    case 'discountPrice':
      return props.collection.showDiscount
    default:
      return true
  }
}

function isZero(part: PartRow, column: string) {
  switch (column) {
    case 'retailPrice':
      return part.retailAmount === 0
    case 'wholesalePrice':
      return part.wholesaleAmount === 0
    case 'discountPrice':
      return part.discountAmount === 0
    default:
      return false
  }
}

function handleClickHidePrice(column: string) {
  switch (column) {
    case 'retailPrice':
      emit('toggleShowRetailPrice')
      break
    case 'wholesalePrice':
      emit('toggleShowWholesalePrice')
      break
    case 'discountPrice':
      emit('toggleShowDiscountPrice')
      break
  }
}

// --------------------------------------------------
// Part cell display / navigation
// --------------------------------------------------

function isPartLinkColumn(column: string) {
  const links = new Set(['partNumber', 'name'])
  return links.has(column)
}

function getPartHref(part: PartRow) {
  return {
    path: `/part/${part.id}/details`
  }
}

function getTagValues(part: PartRow, column: string) {
  return part.tags?.[column.slice(4)]?.join(', ')
}
</script>

<style scoped>
.collection-header {
  background-color: hsl(0, 0%, 96%);
  color: black;
  padding: 0.5rem 0.2rem;
  border-radius: 3px;
  box-shadow: 3px 2px 2px 0 rgba(122, 122, 122, 0.3);
}

.collection-toggle-btn {
  color: black;
  background-color: transparent;
  height: auto;
  padding: 0.25rem;
}

.collection-toggle-btn,
.collection-toggle-btn:hover,
.collection-toggle-btn:focus,
.collection-toggle-btn:active {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.collection-toggle-btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Parts list */

.parts-list-wrapper {
  overflow: auto;
  cursor: grab;
  background: white;
  border: 1px solid lightgrey;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  -webkit-overflow-scrolling: touch;
}

.parts-list-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.parts-list-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.parts-list-wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.parts-header-row {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: stretch;
  background: white;
  border-bottom: 1px solid #dbdbdb;
  cursor: grab;
}

.price-header .eye-icon {
  vertical-align: middle;
  margin-left: 0.25rem;
}

.part-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid lightgrey;
  cursor: grab;
}

.part-row--alt {
  background-color: hsl(0, 0%, 98%);
}

.part-cell {
  flex-shrink: 0;
  flex-grow: 1;
  padding: 0.25rem;
  border-right: 1px solid lightgrey;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  box-sizing: border-box;
  white-space: normal;
  word-break: break-word;
  cursor: grab;
}

.part-cell:last-child {
  border-right: none;
}

.parts-header-row .part-cell {
  font-weight: 600;
  word-break: normal;
}

.no-results-row {
  padding: 0.5rem;
  text-align: center;
  color: #7a7a7a;
}

.thumbnail-cell {
  white-space: nowrap;
  justify-content: center;
}

.eye-icon:hover {
  cursor: pointer;
}

.part-row a,
.part-row .pointer-on-hover {
  cursor: pointer;
}

.part-row a span {
  cursor: pointer;
}

.part-row span,
.part-row label,
.parts-header-row span,
.parts-header-row label {
  cursor: text;
  user-select: text;
}

@media screen and (max-width: 768px) {
  .parts-list-wrapper {
    overflow-x: auto;
    overflow-y: visible;
    max-height: none;
  }
}
</style>
